import { FC, useCallback, useMemo } from 'react';
import { ModalContainer, ModalHeader } from '@/utils/modal';
import { AddSamplePlansForm } from '@/components/Samples/AddSamplePlansForm';
import { AddSamplePlanProvider } from '@/components/Samples/AddSamplePlanProvider';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { StudyApiId } from '@/model/Study.model';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import { DatabaseId } from '@/model/Common.model';
import Loading from '@/components/Loading/Loading';
import { StudyGroupV1 } from '@/model/TreatmentGroup.model';
import { errorToast, successToast } from '@/helpers';
import {
  PlannedAnimalTarget,
  RegisteredAnimalTarget,
  SampleDefinitionApiId,
  SampleDefinitionCreateV1,
  SampleDefinitionFormData,
  SampleDefinitionTargetFormData,
  SampleDefinitionV1,
} from '@/model/SamplePlan.model';
import { components } from '@/generated/internal-api/openapi-types';
import { isApiId } from '@/utils/ApiId.utils';
import { SampleDefinitionTargetV1 } from '@/components/Samples/AssignAnimals/AssignAnimal.util';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';
import { arraysEqual } from '@/utils/array.utils';
import { detailsEqual, metadataEqual, targetsEqual } from '@/components/Samples/SampleDefinition.utils';

type AddSamplePlansFormModalProps = {
  studyId: DatabaseId;
  studyApiId: StudyApiId;
  sampleDefinitions: Array<SampleDefinitionV1>;
  onSampleDefinitionsUpdate: () => void;
  closeModal: () => void;
};
/**
 * Converts form target data to the API target format.
 * Validates targets by API ID prefix, and for planned animals additionally
 * filters out deleted animal definitions using the current planned_animals list.
 */
const toSampleDefinitionTargetV1 = (
  data: SampleDefinitionTargetFormData,
  planned_animals: Array<PlannedAnimalTarget>
): SampleDefinitionTargetV1 => {
  switch (data.type) {
    case 'groups': {
      return { type: data.type, targets: data.targets.filter((api_id) => isApiId('grp', api_id, false)) };
    }
    case 'registered_animals': {
      return { type: data.type, targets: data.targets.filter((api_id) => isApiId('aml', api_id, false)) };
    }
    case 'planned_animals': {
      // Filter out deleted animal definitions by checking if they exist in current planned_animals
      const validAnimalDefinitionIds = new Set(planned_animals.map((animal) => animal.api_id));
      return {
        type: data.type,
        targets: data.targets.filter(
          (api_id) => isApiId('adf', api_id, false) && validAnimalDefinitionIds.has(api_id)
        ) as Array<AnimalDefinitionApiId>,
      };
    }
    default: {
      return { type: 'no_target' };
    }
  }
};
/**
 * Compares a sample definition payload against its original server value and returns
 * only the fields that have changed. Returns null if no changes detected.
 * Note: sample_type is immutable and not included in updates.
 */
const diffSampleDefinitionUpdate = (
  api_id: SampleDefinitionApiId,
  payload: Omit<SampleDefinitionCreateV1, 'sample_type'>,
  original: SampleDefinitionV1
): components['schemas']['SampleDefinitionUpdateV1.schema'] | null => {
  const changes: Partial<components['schemas']['SampleDefinitionUpdateV1.schema']> = { api_id };
  let hasChanges = false;

  if (payload.name !== original.name) {
    changes.name = payload.name;
    hasChanges = true;
  }

  if (!arraysEqual(payload.timepoints, original.timepoints, (x, y) => x === y)) {
    changes.timepoints = payload.timepoints;
    hasChanges = true;
  }

  if (!detailsEqual(payload.details, original.details)) {
    changes.details = payload.details;
    hasChanges = true;
  }

  if (!metadataEqual(payload.metadata, original.metadata)) {
    changes.metadata = payload.metadata;
    hasChanges = true;
  }

  if (payload.calculate_collection_time !== original.calculate_collection_time) {
    changes.calculate_collection_time = payload.calculate_collection_time;
    hasChanges = true;
  }

  if (payload.target && !targetsEqual(payload.target, original.target)) {
    changes.target = payload.target;
    hasChanges = true;
  }

  return hasChanges ? (changes as components['schemas']['SampleDefinitionUpdateV1.schema']) : null;
};

/**
 * Partitions sample definition form data into creates, updates, and deletes
 * for the atomic mutate endpoint. For updates, only includes fields that changed
 * from the original server data to reduce data transfer.
 */
const toMutateSampleDefinitionRequest = (
  sampleDefinitions: Array<SampleDefinitionFormData>,
  deleted: Set<SampleDefinitionFormData['api_id']>,
  planned_animals: Array<PlannedAnimalTarget>,
  originalDefinitions: Array<SampleDefinitionV1>
): {
  creates: Array<SampleDefinitionCreateV1>;
  updates: Array<components['schemas']['SampleDefinitionUpdateV1.schema']>;
  deletes: Array<SampleDefinitionApiId>;
} => {
  const creates: Array<SampleDefinitionCreateV1> = [];
  const updates: Array<components['schemas']['SampleDefinitionUpdateV1.schema']> = [];

  const originalMap = new Map<SampleDefinitionApiId, SampleDefinitionV1>();
  originalDefinitions.forEach((def) => {
    originalMap.set(def.api_id, def);
  });

  sampleDefinitions.forEach((def) => {
    const { api_id, calculate_collection_time, sample_type, ...rest } = def;
    const target = toSampleDefinitionTargetV1(rest.target, planned_animals);

    const createPayload: SampleDefinitionCreateV1 = {
      ...rest,
      sample_type,
      calculate_collection_time: calculate_collection_time ?? false,
      target,
    };
    const updatePayload = {
      ...rest,
      calculate_collection_time: calculate_collection_time ?? false,
      target,
    };

    if (isApiId('sdf', api_id, false)) {
      const original = originalMap.get(api_id);
      if (original) {
        const diff = diffSampleDefinitionUpdate(api_id, updatePayload, original);
        if (diff) {
          updates.push(diff);
        }
      } else {
        updates.push({ api_id, ...updatePayload });
      }
    } else {
      creates.push(createPayload);
    }
  });

  const deletes = Array.from(deleted).filter((api_id): api_id is SampleDefinitionApiId =>
    isApiId('sdf', api_id, false)
  );

  return { creates, updates, deletes };
};

/**
 * Modal for creating and editing study sampling plans.
 * Handles sample type definitions, metadata configuration, and group/animal assignments.
 * Uses atomic mutation endpoint for creates/updates/deletes in a single transaction.
 */
export const AddSamplePlansFormModal: FC<AddSamplePlansFormModalProps> = ({
  studyId,
  studyApiId,
  sampleDefinitions,
  onSampleDefinitionsUpdate,
  closeModal,
}) => {
  const { loading: registeredAnimalsLoading, response: registeredAnimalsResponse } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyId}/animals',
    path: { studyId: studyApiId },
    query: { include: ['study_group'], perPage: -1 },
    invokeOnInit: true,
    options: { onError: { slug: 'sample-plan-crud-registered-animal-load' } },
  });
  const { loading: plannedAnimalsLoading, response: plannedAnimalsResponse } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/planned/animals',
    path: { studyApiId },
    query: { perPage: -1 },
    options: { onError: { slug: 'sample-plan-crud-planned-animal-load' } },
  });
  const { loading: groupsLoading, response: groupsResponse } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'studies.groups.list',
    path: { id: studyId },
    query: { perPage: -1 },
    options: { onError: { slug: 'sample-plan-crud-groups-load' } },
  });
  const { invoke: mutateSampleDefinitions } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/mutate',
    path: { studyApiId },
    invokeOnInit: false,
    options: {
      onError: {
        slug: 'sample-plan-crud-sample-definition-mutate',
        capture: true,
        throw: false,
        toast: false,
      },
    },
  });
  const planned_animals = useMemo<Array<PlannedAnimalTarget>>(() => {
    if (plannedAnimalsResponse?.type === 'success') {
      return plannedAnimalsResponse.body.data.map((def) => ({
        api_id: def.api_id,
        animal_api_id: def.animal_api_id,
        planned_id: def.planned_id,
        study_group_api_id: def.study_group_api_id as PlannedAnimalTarget['study_group_api_id'],
      }));
    }
    return [];
  }, [plannedAnimalsResponse]);
  const registered_animals = useMemo<Array<RegisteredAnimalTarget>>(() => {
    if (registeredAnimalsResponse?.type === 'success') {
      return registeredAnimalsResponse.body.data.map((animal) => ({
        api_id: animal.api_id,
        name: animal.name,
        study_group_api_id: animal.study_group?.api_id,
      }));
    }
    return [];
  }, [registeredAnimalsResponse]);

  const handleSubmit = useCallback(
    async (formData: Array<SampleDefinitionFormData>, deleted: Set<SampleDefinitionFormData['api_id']>) => {
      const { creates, updates, deletes } = toMutateSampleDefinitionRequest(
        formData,
        deleted,
        planned_animals,
        sampleDefinitions
      );

      const mutateResponse = await mutateSampleDefinitions({
        path: { studyApiId },
        body: { creates, updates, deletes },
      });

      if (mutateResponse.type === 'success') {
        successToast('Sample plan updated successfully');
        onSampleDefinitionsUpdate();
        closeModal();
      } else {
        ExceptionHandler.captureException(
          new InVivoError('Sample plan mutation failed', {
            slug: 'sample-plan-crud-sample-definition-mutate',
            cause: mutateResponse.error,
            context: {
              studyApiId,
              createCount: `${creates.length}`,
              updateCount: `${updates.length}`,
              deleteCount: `${deletes.length}`,
              sampleDefinitionNames: formData.map((s) => s.name).join(', '),
              deletedIds: Array.from(deleted).join(', '),
            },
          })
        );

        const errorDetails = mutateResponse.error?.response?.data?.errors;
        if (errorDetails) {
          const errorMessages = Object.entries(errorDetails)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ');
          errorToast(`Could not update sample plan: ${errorMessages}`);
        } else {
          const operationSummary = `${creates.length} creates, ${updates.length} updates, ${deletes.length} deletes`;
          errorToast(`Could not update sample plan (${operationSummary})`);
        }
      }
    },
    [studyApiId, mutateSampleDefinitions, onSampleDefinitionsUpdate, closeModal, planned_animals, sampleDefinitions]
  );

  if (
    groupsLoading ||
    registeredAnimalsLoading ||
    plannedAnimalsLoading ||
    groupsResponse?.type !== 'success' ||
    registeredAnimalsResponse?.type !== 'success' ||
    plannedAnimalsResponse?.type !== 'success'
  ) {
    return <Loading />;
  }

  return (
    <ModalContainer size="wide">
      <ModalHeader
        title="Sampling plan"
        subText={
          <>
            Create a sampling plan by defining sample types, specifying metadata, and assigning them to groups or
            animals.{' '}
            <a
              className="link"
              href="https://help.benchling.com/hc/en-us/articles/41869571707533-Benchling-In-Vivo-Animal-and-Sample-Planning-Limited-Availability"
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
            </a>
            .
          </>
        }
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        subTextClasses="f6 lh-copy mt2"
      />
      <div className="mh4 mt3">
        <AddSamplePlanProvider
          groups={(groupsResponse.body as { data: Array<StudyGroupV1> }).data}
          registered_animals={registered_animals}
          planned_animals={planned_animals}
          sample_definitions={sampleDefinitions}
          onSubmit={handleSubmit}
        >
          <AddSamplePlansForm />
        </AddSamplePlanProvider>
      </div>
    </ModalContainer>
  );
};
